import { create } from "zustand";
import { Quest, QuoQuestLevel, QuoQuestLevelStage } from "../types/quest";
// import { mockLevels } from "./mockLevels";
import { mockLevels } from "./mockLevelsComprehensive";
// import { equalTo, onValue, orderByChild, query, ref } from "firebase/database";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useAuthStore } from "./auth.store";
import {
  convertTimestampsToDate,
  firestore,
  setFirebaseDocument
} from "../lib/firebase";
import { useGlobalsStore } from "./globals.store";
import { randomizeIcon } from "../utils/botUtils";

interface questState {
  levels: QuoQuestLevel[];
  id?: string;
  bot?: any;
  name?: string;
  loading: boolean;
  updateLevelStage(level: number, stage: QuoQuestLevelStage): void;
}

export const useQuestsStore = create<questState>(set => ({
  // TODO: Remove mock data once DB/API are ready
  levels: [],
  // levels: mockLevels,
  loading: false,
  updateLevelStage(level: number, stage: QuoQuestLevelStage) {
    set(({ levels }) => {
      // Which level are we updating
      const levelToUpdate = levels.find(
        stateLevel => stateLevel.level === level
      );
      if (levelToUpdate) {
        // Replace matched stage with updated stage
        levelToUpdate.stages = levelToUpdate.stages.map(stateStage =>
          stateStage.stageId !== stage.stageId ? stateStage : stage
        );
        // It stands to reason that we can use the latest updated "lockedUntil"
        levelToUpdate.lockedUntil = stage.lockedUntil;
        // If all stages are complete, the level is complete
        const isComplete = levelToUpdate.stages.reduce(
          (isComplete, currentStage) => isComplete && currentStage.hasCleared,
          true
        );
        if (isComplete) {
          levelToUpdate.status = "complete";
          levelToUpdate.completionPercent = 100;
        } else {
          // If the level is incomplete, the completion percentage is (count of complete stages * 100 / count of all stages)
          levelToUpdate.completionPercent =
            (levelToUpdate.stages.reduce(
              (countComplete, currentStage) =>
                countComplete + (currentStage.hasCleared ? 1 : 0),
              0
            ) *
              100) /
            levelToUpdate.stages.length;
        }
        // Return all the levels as they were except for the newly updated level
        // TODO: Update DB maybe?
        return {
          levels: levels.map(stateLevel =>
            stateLevel.level !== level ? stateLevel : levelToUpdate
          )
        };
      }

      // In case some jank happened and we haven't matched a level, return current state
      return {
        levels
      };
    });
  }
}));

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Function to create a default quest
async function createDefaultQuest(userId: string) {
  await delay(5000);
  console.log("Creating default quest");
  const globalState = await useGlobalsStore.getState();
  const defaultBot = JSON.parse(
    JSON.stringify(globalState?.globals?.defaultBotConfig)
  );
  defaultBot.tradeParameters.tickerInterval = "30m";
  delete defaultBot?.weightedCoins;
  defaultBot.whitelist = ["BTC", "ETH"];

  const quest = {
    name: "QuoQuest",
    version: "0.0.1",
    user: userId,
    levels: mockLevels,
    bot: {
      ...defaultBot,
      quest: true,
      img: randomizeIcon()
    }
  };

  await setFirebaseDocument("quests", quest);
}

useAuthStore.subscribe(authState => {
  if (!authState.user) return;

  const userActiveQuestRef = query(
    collection(firestore, "quests"),
    where("user", "==", authState.user.uid)
  );

  console.log("Starting up Quest store");

  onSnapshot(userActiveQuestRef, async snapshot => {
    if (snapshot.empty && authState.user) {
      // No quests found, create default quest
      await createDefaultQuest(authState.user.uid);
    } else {
      // Quests found, process them
      const userQuests: Quest[] = [];

      for (const doc of snapshot.docs) {
        useQuestsStore.setState({ loading: true });

        const rawData = doc.data();
        const quest: Quest = convertTimestampsToDate(rawData) as Quest;
        const questId = doc.id;

        if (quest) {
          userQuests.push({ id: questId, ...quest });
        }
      }
      useQuestsStore.setState(() => ({
        levels: userQuests[0].levels,
        bot: userQuests[0].bot,
        name: userQuests[0].name,
        id: userQuests[0].id,
        loading: false
      }));
    }
  });
});

// TODO: Some kind of subscribeToLevel given levelId as a param (see bot store)
